import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Suppress duplicate "session expired" toasts if a page fires multiple
  // 401s in quick succession (e.g. dashboard triggers 3 authed fetches
  // in parallel).
  const expiredShownRef = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      expiredShownRef.current = false;
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Listen for 401s broadcast by the axios interceptor. Any authenticated
  // API call that suddenly starts 401ing means the server session is
  // gone — clear the local user so the UI matches reality.
  useEffect(() => {
    const onExpired = () => {
      // Only react if we currently *think* we're logged in.
      setUser((prev) => {
        if (!prev) return prev;
        if (!expiredShownRef.current) {
          expiredShownRef.current = true;
          toast.error("Your session expired. Please sign in again.");
        }
        return null;
      });
    };
    window.addEventListener("sl:session-expired", onExpired);
    return () => window.removeEventListener("sl:session-expired", onExpired);
  }, []);

  const loginWithDiscord = async () => {
    try {
      const { data } = await api.get("/auth/discord/url", {
        params: { origin: window.location.origin },
      });
      // Break out of any parent iframe (Emergent preview) — Discord refuses to be framed
      try {
        window.top.location.href = data.url;
      } catch {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error("Discord OAuth init failed", e);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(null);
    window.location.href = "/";
  };

  // Alias for legacy call sites — always Discord now.
  const login = loginWithDiscord;

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, loginWithDiscord, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
